// What a branch panel says, as a pure model (PLAN §19.5).
//
// `BranchPicker.tsx` is the pixels; this is the question of *what the offers
// differ about*, which is a question about the game and therefore testable
// without a browser. Same split as `celebrations.ts` and `pieceBadges.ts`, and
// for the same reason.
//
// **The panel's whole job is to make the choice legible.** An offer that reads
// as "camp A or camp B" is a coin flip, and §19.6 hazard 1 is that a dominated
// offer is not a choice at all — `waveOptions` already refuses to present one,
// so by the time a pair arrives here they *do* disagree somewhere, and this
// finds where. Four axes, every one of them a number the model already has:
// how hard, how much ground, what is on it, which way.
//
// **Differences are marked, not scored.** A single number per offer would be a
// recommendation, and the point is that the player has an opinion the
// evaluation does not: a bigger fight is worse if you are hurt and better if
// you want the scrap, and a seam is not a magnitude at all.

import type { Branch, OfferMark } from "src/api";
import { EAST, NORTH, SOUTH, WEST } from "src/engine/rules/board";
import type { StageOffers } from "src/engine/ai/runStep";
import type { WaveOffer } from "src/engine/ai/waves";
import { EMPTY_TALLY, type StageTally } from "src/engine/rules/tally";
import { statsOf } from "./celebrations";
import { rarityOfOffer } from "src/engine/session/rarity";

// `OfferAxis`, `OfferMark`, `Offer` and `Branch` are the contract's
// (`src/api/drivers.ts`): the panel is the client's and reads them off
// `Events.branch`, so the words are defined once, on the line.
export type { Branch, Offer, OfferAxis, OfferMark } from "src/api";

/** Which way the board grows, as words. `buildWave`'s facing is a `Direction`. */
const HEADINGS: Record<number, string> = {
  [NORTH]: "north",
  [EAST]: "east",
  [SOUTH]: "south",
  [WEST]: "west"
};

const heading = (facing: number): string => HEADINGS[facing] ?? "onward";

/**
 * How a camp's material reads as a fight.
 *
 * Banded rather than printed, because the number is centipawns of formation
 * and nobody is choosing between 2,400 and 2,750. What a player wants is the
 * word they would have used.
 */
function weight(fight: number): string {
  if (fight >= 3000) return "Heavy";
  if (fight >= 1800) return "Strong";
  if (fight >= 900) return "Fair";
  return "Light";
}

/** `1` if this is strictly the largest of the values, `-1` the smallest, else `0`. */
function edgeOf(value: number, all: readonly number[]): number {
  if (all.length < 2) return 0;
  const most = Math.max(...all);
  const least = Math.min(...all);
  if (most === least) return 0;
  if (value === most) return 1;
  if (value === least) return -1;
  return 0;
}

/**
 * The axis this offer most obviously wins, and the sentence for it.
 *
 * Checked in a fixed order so the same pair always reads the same way, and the
 * seam comes last: it is the axis every offer differs on when the others tie,
 * so leading with it would bury a real difference behind a compass point.
 */
function reasonFor(mine: WaveOffer, theirs: readonly WaveOffer[]): string {
  if (theirs.length === 0) return "";
  const fewestFight = theirs.every((other) => mine.fight < other.fight);
  const mostFight = theirs.every((other) => mine.fight > other.fight);
  const mostGround = theirs.every((other) => mine.ground > other.ground);
  const mostPrize = theirs.every((other) => mine.prize > other.prize);
  const otherWay = theirs.every((other) => mine.facing !== other.facing);
  const lessGround = theirs.every((other) => mine.ground < other.ground);
  // Identical on everything a magnitude can express. Worth its own answer,
  // because it is the one case where the seam really is the whole story.
  const tied = theirs.every(
    (other) =>
      mine.fight === other.fight &&
      mine.ground === other.ground &&
      mine.prize === other.prize
  );

  if (mostPrize && mine.prize > 0) return "The promotion ground is here.";
  if (mostGround) return "The most ground to build on.";
  if (fewestFight) return "The lighter fight.";
  if (mostFight) return "The harder fight, and the scrap it leaves.";
  // **This offer wins nothing, so its argument is where it is** — which is
  // exactly why `dominates` refuses to rank two camps facing different ways.
  //
  // The sentence has to be *true of this pair*, and the first version was not:
  // it said "same fight either way, only the direction differs" whenever an
  // offer won no axis, including when it plainly had less ground than the one
  // beside it. Driven, the panel showed 24 squares against 34 and told the
  // player they were the same. So the tie is tested rather than assumed.
  if (otherWay) {
    if (tied) return "Same fight either way. Only the direction differs.";
    if (lessGround) return `Less ground, but it opens ${heading(mine.facing)}.`;
    return `Take it for where it opens, ${heading(mine.facing)}.`;
  }
  return "";
}

function marksFor(offer: WaveOffer, all: readonly WaveOffer[]): OfferMark[] {
  // **No promotion axis where nobody offers one** — a run has no promotion
  // (§31 P2), and a column of dashes claims a mechanic the game lacks.
  const prized = all.some((one) => one.prize > 0);
  const marks: OfferMark[] = [
    {
      axis: "fight",
      label: "Fight",
      value: weight(offer.fight),
      // **Inverted on purpose.** Every other axis is a thing you get more of;
      // this is a thing that is done to you, so the offer with the *smallest*
      // camp carries the good mark and the panel reads consistently — a green
      // pip means "this one is easier here", whatever "here" is.
      // **`|| 0`, because negating zero gives `-0`**, which is not `0` to
      // `Object.is` and so not to a test, a `Map` key or a snapshot. A sign
      // that only ever means "which way" has no business carrying two zeroes.
      edge: -edgeOf(offer.fight, all.map((one) => one.fight)) || 0
    },
    {
      axis: "ground",
      label: "Ground",
      value: `${offer.ground}`,
      edge: edgeOf(offer.ground, all.map((one) => one.ground))
    },
    {
      axis: "prize",
      label: "Promotion",
      value: offer.prize > 0 ? `${offer.prize}` : "—",
      edge: edgeOf(offer.prize, all.map((one) => one.prize))
    },
    {
      // A *category*, never a magnitude: which way the board grows decides how
      // far the next front is and which of your pieces are near it, and no
      // ordering of the other three captures that. So it is never marked.
      axis: "seam",
      label: "Opens",
      value: heading(offer.facing),
      edge: 0
    }
  ];
  return prized ? marks : marks.filter((mark) => mark.axis !== "prize");
}

/**
 * The panel, given the offers and the front that has just been cleared.
 *
 * The tally is the front's, not the run's: `next-stage` has not landed yet at
 * the moment this is shown, so `state.tally` is still the fight the player has
 * just finished — which is precisely what the card wants to report.
 */
export function branchView(
  stage: number,
  offers: readonly WaveOffer[],
  tally: StageTally,
  /** Ground coming back, when the board has been eaten past a camp fitting. */
  grow?: { depth: number }
): Branch {
  return {
    stage,
    stats: statsOf(tally),
    // A choice either way: one camp and the option of no camp is still two
    // things to pick between.
    only: offers.length + (grow ? 1 : 0) <= 1,
    grow: grow
      ? {
          id: "branch-offer-grow",
          index: offers.length,
          rarity: rarityOfOffer({ kind: "regrowth" }),
          title: "The ground returns",
          heading: `Reaches ${grow.depth} squares out`,
          marks: [],
          detail:
            `No camp — there is nowhere left to put one. New ground rises out ` +
            `of the dark beside your army and spreads outward, and some of it ` +
            `comes up as somewhere a pawn can be sent.`
        }
      : undefined,
    offers: offers.map((offer, index) => ({
      id: `branch-offer-${index}`,
      index,
      title: offer.wave.formation,
      heading: `Opens ${heading(offer.facing)}`,
      // Ground worth fighting over is scarcer than an ordinary camp.
      rarity: rarityOfOffer({ kind: "camp", prize: offer.prize }),
      marks: marksFor(offer, offers),
      detail: reasonFor(
        offer,
        offers.filter((_, at) => at !== index)
      )
    }))
  };
}

/**
 * The panel for a front the driver has stopped on.
 *
 * `tally` is optional on the state — a game outside a run has none — and the
 * empty one reports four zeroes rather than crashing a modal the player cannot
 * get past.
 */
export const branchOf = (front: StageOffers, tally: StageTally | undefined): Branch =>
  branchView(
    front.stage,
    front.offers,
    tally ?? EMPTY_TALLY,
    front.grow && { depth: front.grow.depth }
  );
